import React, { useMemo } from 'react';
import { Button } from   '@/components/ui/button';
import { IconProvider } from   '@/components/ui';
import { DialogSection, DialogOption, ConditionOption, DialogField, DialogVariable } from '@/lib/stores/dialogStore';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DialogSectionEditor } from './DialogSectionEditor';

interface DialogSectionsListProps {
  sections: DialogSection[];
  sectionIdentifiers: string[];
  currentDialogId?: string;
  dialogTranslatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  dialogVariables?: DialogVariable[];
  currentSectionIndex?: number;
  onSectionClick?: (index: number) => void;
  onAddSection: () => void;
  onUpdateSection: (index: number, updates: Partial<DialogSection>) => void;
  onRemoveSection: (index: number) => void;
  onAddField: (sectionIndex: number) => void;
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  onAddOption: (sectionIndex: number, fieldId: string) => void;
  onUpdateOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<DialogOption>) => void;
  onRemoveOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  onAddConditionOption: (sectionIndex: number, fieldId: string) => void;
  onUpdateConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<ConditionOption>) => void;
  onRemoveConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  onSectionDragEnd: (event: DragEndEvent) => void;
  gameId?: string;
  t: any;
}

export const DialogSectionsList = React.memo(function DialogSectionsList({
  sections,
  sectionIdentifiers,
  currentDialogId,
  dialogTranslatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  currentSectionIndex = 0,
  onSectionClick,
  onAddSection,
  onUpdateSection,
  onRemoveSection,
  onAddField,
  onUpdateField,
  onRemoveField,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  onAddConditionOption,
  onUpdateConditionOption,
  onRemoveConditionOption,
  onSectionDragEnd,
  gameId,
  t,
}: DialogSectionsListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Memoize a lightweight sections summary for child components.
  // Child editors only need { section_identifier, name } for navigation/jump targets,
  // not the full DialogSection with all fields. This prevents ALL section editors
  // from re-rendering when only one field's text changes (which creates a new
  // sections array reference but doesn't change identifiers or names).
  const sectionsSummaryKey = sections.map(s => `${s.section_identifier}|${s.name}`).join('\n');
  const sectionsSummary = useMemo(
    () => sections.map(s => ({ section_identifier: s.section_identifier, name: s.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sectionsSummaryKey]
  );

  return (
    <div className="space-y-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onSectionDragEnd}
      >
        <SortableContext
          items={sections.map(s => s.id || `section-fallback-${s.section_identifier}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {sections.map((section, sectionIndex) => (
              <DialogSectionEditor
                key={section.id || `section-fallback-${section.section_identifier}`}
                section={section}
                sectionIndex={sectionIndex}
                sectionIdentifiers={sectionIdentifiers}
                sections={sectionsSummary}
                currentDialogId={currentDialogId}
                dialogTranslatable={dialogTranslatable}
                speakerMode={speakerMode}
                speakerTypeId={speakerTypeId}
                speakerTemplateIds={speakerTemplateIds}
                dialogVariables={dialogVariables}
                isSelected={sectionIndex === currentSectionIndex}
                onSectionClick={() => onSectionClick?.(sectionIndex)}
                onUpdateSection={onUpdateSection}
                onRemoveSection={onRemoveSection}
                onAddField={onAddField}
                onUpdateField={onUpdateField}
                onRemoveField={onRemoveField}
                onAddOption={onAddOption}
                onUpdateOption={onUpdateOption}
                onRemoveOption={onRemoveOption}
                onAddConditionOption={onAddConditionOption}
                onUpdateConditionOption={onUpdateConditionOption}
                onRemoveConditionOption={onRemoveConditionOption}
                gameId={gameId}
                t={t}
                totalSections={sections.length}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add Section Button at the bottom */}
      <div className="flex items-center justify-center border-t border-border pt-4">
        <Button onClick={onAddSection} size="sm" variant="outline" type="button">
          <IconProvider iconstring="fa-plus" className="mr-2" size={14} />
          Add Section
        </Button>
      </div>
    </div>
  );
});

